<template>
  <div>
    <div class="item-summary-row">
      <div class="item-summary-cell">
        <img class="icon-small mr-3" v-bind:src="getFeedTypeIcon(feed.feedType)" />
        <strong>{{ feed.name }}</strong>
      </div>
      <div class="item-summary-cell open-file-copy-link-container">
        <div class="d-flex flex-column">
          <a :href="feedsDirectoryUrl + feed.id+'.'+feed.config.resultType" target="_blank" class="mr-4 pr-3">
            <link-icon></link-icon>
            <span class="ml-2">{{ $t('feeds.open_feed') }}</span>
          </a>
          <a v-if="feed.projectId == 13 || feed.projectId == 64" :href="feedsDirectoryUrl + feed.id+'.csv'" target="_blank" class="mr-4 pr-3">
            <link-icon></link-icon>
            <span class="ml-2">{{ $t('feeds.open_feed') }} (csv)</span>
          </a>
        </div>
        <div :id="'copy-feed-url-'+feed.id" class="d-flex mb-0 cursor-pointer copy-link-container" v-clipboard:copy="feedsDirectoryUrl + feed.id+'.'+feed.config.resultType" v-clipboard:success="onCopy('copy-feed-url-'+feed.id)">
          <copy-icon></copy-icon>
          <p class="ml-2 mb-1">{{ $t('feeds.copy_link') }}</p>
        </div>
        <b-popover :target="'copy-feed-url-'+feed.id" variant="success" triggers="click" placement="bottomleft">
          <div>
            <p class="mb-0">{{ $t('feeds.url_copied_to_clipboard') }}</p>
          </div>
        </b-popover>
      </div>
      <div v-if="feed.logs.length > 0" class="item-summary-cell">
        <div v-if="alerts.feed !== undefined && alerts.feed.filter(alert => alert.feedId === feed.id).length > 0">
          <b-popover :target="'tooltip-feed-alerts'+feed.id" variant="danger" triggers="hover" placement="bottomleft">
            <div v-for="alert in alerts.feed.filter(alert => alert.feedId === feed.id)" v-bind:key="alert.alertId">
              <p>{{ alert.message }}</p>
            </div>
          </b-popover>
          <router-link :to="{ name: 'feed.details', params: { id: this.$route.params.id, feedId: feed.id, feed: feed }}">
            <img v-if="alerts.feed.filter(alert => alert.feedId === feed.id).filter(alert => alert.level === 0).length > 0" class="icon cursor-pointer" :id="'tooltip-feed-alerts'+feed.id" src="/app/images/icons/warning_danger.svg" />
            <img v-else-if="alerts.feed.filter(alert => alert.feedId === feed.id).filter(alert => alert.level === 1).length > 0" class="icon cursor-pointer" :id="'tooltip-feed-alerts'+feed.id" src="/app/images/icons/warning.svg" />
          </router-link>
        </div>
        <div v-else>
          <router-link :to="{ name: 'feed.details', params: { id: this.$route.params.id, feedId: feed.id, feed: feed }}">
            <img class="icon" src="/app/images/icons/success.svg" />
          </router-link>
        </div>
        <div>
          <p class="mb-2"><strong>{{ feed.logs[0].productCount }} {{ $t('global.products') }} (<span v-if="feed.logs[0].change > 0">+</span>{{ feed.logs[0].change }})</strong></p>
          <p>{{ countHoursAgo(feed.logs[0].date, new Date()) }} {{ $t('global.hours_ago') }}</p>
        </div>
      </div>
      <div v-else class="item-summary-cell">
        <img class="icon" src="/app/images/icons/warning.svg" />
        <div>
          <p class="mb-0 text-orange-dark">{{ $t('feeds.first_feed_update_warning') }}</p>
        </div>
      </div>
      <div class="item-summary-cell"></div>
      <div class="item-summary-cell toggle-button-container justify-content-end">
        <button class="btn btn-gray btn-toggle" @click="toggleFeed">
          <img v-bind:class="[isHidden ? '' : 'rotated']" src="/app/images/icons/toggle.svg">
        </button>
      </div>
    </div>
    <transition name="slide-down">
      <div v-show="!isHidden">
        <div v-if="feed.logs.length > 0">
          <div class="d-flex pt-5">
            <div class="item-summary-label"><p>{{ $t('sources.added') }}</p></div>
            <div><p class="text-green">{{ feed.logs[feed.logs.length - 1].date.substr(0, 10) }}</p></div>
          </div>
          <div class="d-flex">
            <div class="item-summary-label"><p>{{ $t('projects.last_update') }}</p></div>
            <div><p class="mb-4"><span class="text-green">{{ countHoursAgo(feed.logs[0].date, new Date()) }} {{ $t('global.hours_ago') }}</span></p></div>
          </div>
        </div>
        <div v-else class="d-flex align-items-center pt-5 pb-3">
          <img class="icon" src="/app/images/icons/warning.svg" />
          <div>
            <p class="mb-0 text-orange-dark">{{ $t('feeds.first_feed_update_info') }}</p>
          </div>
        </div>
        <div class="d-flex justify-content-between feed-actions-container">
          <div class="d-flex pt-4">
            <router-link :to="{ name: 'feed.edit.field-mapping', params: { projectId: this.$route.params.id, feedId: feed.id, feed: feed }}" class="mr-4" v-bind:class="{ 'disabled-router-link': noProducts }">
              <button :disabled="noProducts" type="button" class="btn btn-small-medium btn-orange">
                <server-icon></server-icon>
                <span>{{ $t('feeds.field_mapping') }}</span>
              </button>
            </router-link>
            <router-link :to="{ name: 'feed.edit.rules', params: { projectId: this.$route.params.id, feedId: feed.id, feed: feed }}" class="mr-4" v-bind:class="{ 'disabled-router-link': noProducts }">
              <button :disabled="noProducts" type="button" class="btn btn-small-medium btn-orange">
                <list-icon></list-icon>
                <span>{{ $t('feeds.rules') }}</span>
              </button>
            </router-link>
            <router-link :to="{ name: 'feed.edit.filters', params: { projectId: this.$route.params.id, feedId: feed.id, feed: feed }}" class="mr-4" v-bind:class="{ 'disabled-router-link': noProducts }">
              <button :disabled="noProducts" type="button" class="btn btn-small-medium btn-orange">
                <filter-icon></filter-icon>
                <span>{{ $t('feeds.filters') }}</span>
              </button>
            </router-link>
            <router-link :to="{ name: 'feed.edit.tagging', params: { projectId: this.$route.params.id, feedId: feed.id, feed: feed }}" class="mr-4" v-bind:class="{ 'disabled-router-link': noProducts }">
              <button :disabled="noProducts" type="button" class="btn btn-small-medium btn-orange">
                <tag-icon></tag-icon>
                <span>{{ $t('feeds.tagging') }}</span>
              </button>
            </router-link>
            <button v-if="feed.active" type="button" class="btn btn-small-medium btn-gray-dark" @click="editFeedStatus">
              <pause-icon></pause-icon>
              <span>{{ $t('projects.deactivate') }}</span>
            </button>
            <button v-else type="button" class="btn btn-small-medium btn-orange-dark" @click="editFeedStatus">
              <play-icon></play-icon>
              <span>{{ $t('projects.activate') }}</span>
            </button>
          </div>
          <div class="d-flex pt-4">
            <router-link :to="{ name: 'feed.details', params: { id: this.$route.params.id, feedId: feed.id, feed: feed }}">
              <button type="button" class="btn btn-small-medium btn-orange">
                <eye-icon></eye-icon>
                <span>{{ $t('feeds.more_info') }}</span>
              </button>
            </router-link>
            <button type="button" class="btn btn-small-medium btn-danger ml-4" @click="deleteFeed">
              <trash-2-icon></trash-2-icon>
              <span>{{ $t('feeds.delete_feed') }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import dates from '@/mixins/dates'
import { CopyIcon, EyeIcon, FilterIcon, LinkIcon, ListIcon, PauseIcon, PlayIcon, ServerIcon, TagIcon, Trash2Icon } from 'vue-feather-icons'

export default {
  name: 'FeedSummary',

  data () {
    return {
      isHidden: true
    }
  },

  props: {
    alerts: { required: true, type: Object },
    feed: { required: true, type: Object },
    feedsDirectoryUrl: { required: true, type: String },
    feedTypes: { required: true, type: Array },
    noProducts: { required: true, type: Boolean }
  },

  mixins: [dates],

  methods: {
    toggleFeed () {
      this.isHidden = !this.isHidden
    },

    editFeedStatus () {
      this.$emit('onFeedStatusUpdate', this.feed)
    },

    deleteFeed () {
      this.$emit('onFeedDelete', this.feed.id)
    },

    getFeedTypeIcon (feedTypeName) {
      return this.feedTypes.filter(feedType => feedType.name === feedTypeName)[0].imageUrl
    },

    onCopy (popoverId) {
      setTimeout(function () {
        this.$root.$emit('bv::hide::popover', popoverId)
      }.bind(this), 1500)
    }
  },

  components: { CopyIcon, EyeIcon, FilterIcon, LinkIcon, ListIcon, PauseIcon, PlayIcon, ServerIcon, TagIcon, Trash2Icon }
}
</script>
